import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SyncIcon from "@mui/icons-material/Sync";
import { GetUpload, PostUpload } from "../../services/apiServices";
import { networkUrls } from "../../services/networkurls";

interface Props {
  open: boolean;
  docType: "case-studies" | "videos";
  label: string;
  onClose: () => void;
  onComplete: () => void;
}

type Status = "idle" | "running" | "completed" | "failed";

const POLL_INTERVAL_MS = 10_000;

const ReindexDialog: React.FC<Props> = ({ open, docType, label, onClose, onComplete }) => {
  const [status, setStatus] = useState<Status>("idle");
  const [stage, setStage] = useState("");
  const [filesTotal, setFilesTotal] = useState(0);
  const [filesProcessed, setFilesProcessed] = useState(0);
  const [currentFile, setCurrentFile] = useState("");
  const [error, setError] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [jobId, setJobId] = useState<string | null>(null);

  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimers = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const startElapsed = () => {
    setElapsed(0);
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
  };

  const pollStatus = async (id: string) => {
    const res = await GetUpload(`${networkUrls.reindexStatus}/${id}`, true);
    if (!res) return;
    const data = res.data;
    setStage(data.stage || "");
    setFilesTotal(data.files_total || 0);
    setFilesProcessed(data.files_processed || 0);
    setCurrentFile(data.current_file || "");

    if (data.status === "completed") {
      setStatus("completed");
      clearTimers();
      onComplete();
    } else if (data.status === "failed") {
      setStatus("failed");
      setError(data.error || "Unknown error");
      clearTimers();
    }
  };

  const handleStart = async () => {
    setStatus("running");
    setStage("Starting reindex...");
    setFilesTotal(0);
    setFilesProcessed(0);
    setCurrentFile("");
    setError("");
    setElapsed(0);

    const res = await PostUpload(`${networkUrls.reindex}/${docType}`, {}, true);
    if (!res || !res.data?.job_id) {
      setStatus("failed");
      setError("Failed to start reindex job.");
      return;
    }

    const id = res.data.job_id;
    setJobId(id);
    setStage(res.data.status === "already_running" ? "Already running — attaching to existing job..." : "Job started.");

    startElapsed();
    await pollStatus(id);
    pollRef.current = setInterval(() => pollStatus(id), POLL_INTERVAL_MS);
  };

  useEffect(() => {
    if (!open) {
      clearTimers();
      setStatus("idle");
      setJobId(null);
      setElapsed(0);
    }
    return clearTimers;
  }, [open]);

  const fmtElapsed = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  const progress = filesTotal > 0 ? Math.round((filesProcessed / filesTotal) * 100) : 0;
  const isDone = status === "completed" || status === "failed";

  return (
    <Dialog open={open} onClose={isDone ? onClose : undefined} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: "#1a1a1a" }}>
        Reindex {label}
      </DialogTitle>

      <DialogContent>
        {status === "idle" && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This will clear the existing index and rebuild it from all files in Google Drive.
            </Typography>
            <Box
              sx={{
                backgroundColor: "#fff8e1",
                border: "1px solid #ffe082",
                borderRadius: "8px",
                p: 1.5,
                mb: 3,
              }}
            >
              <Typography variant="body2" color="#b45309" fontWeight={600}>
                ⚠ This operation typically takes 5–15 minutes depending on the number of files.
                Do not close this window while reindexing.
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={handleStart}
                sx={{ backgroundColor: "#4D8FAC", "&:hover": { backgroundColor: "#3b738a" } }}
              >
                Start Reindex
              </Button>
              <Button variant="outlined" onClick={onClose} sx={{ color: "#4D8FAC", borderColor: "#4D8FAC" }}>
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {status === "running" && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <CircularProgress size={20} sx={{ color: "#4D8FAC" }} />
              <Typography variant="body2" fontWeight={600} color="#4D8FAC">
                Reindexing in progress — please keep this window open
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 20 }}>
              {stage}
            </Typography>

            {filesTotal > 0 && (
              <>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">
                    {filesProcessed} / {filesTotal} files
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {progress}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    mb: 1.5,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#e0f0f8",
                    "& .MuiLinearProgress-bar": { backgroundColor: "#4D8FAC", borderRadius: 4 },
                  }}
                />
                {currentFile && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1.5, wordBreak: "break-all" }}
                  >
                    Current: {currentFile}
                  </Typography>
                )}
              </>
            )}

            {filesTotal === 0 && (
              <LinearProgress
                sx={{
                  mb: 1.5,
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: "#e0f0f8",
                  "& .MuiLinearProgress-bar": { backgroundColor: "#4D8FAC", borderRadius: 4 },
                }}
              />
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="caption" color="text.secondary">
                Elapsed: {fmtElapsed(elapsed)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Refreshing status every 10s
              </Typography>
            </Box>
          </Box>
        )}

        {status === "completed" && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <CheckCircleOutlineIcon sx={{ color: "#16a34a", fontSize: 28 }} />
              <Typography variant="body1" fontWeight={700} color="#16a34a">
                Reindex complete!
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {stage}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Total time: {fmtElapsed(elapsed)}
            </Typography>
            <Button
              variant="contained"
              onClick={onClose}
              sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}
            >
              Close
            </Button>
          </Box>
        )}

        {status === "failed" && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
              <ErrorOutlineIcon sx={{ color: "#dc2626", fontSize: 28 }} />
              <Typography variant="body1" fontWeight={700} color="#dc2626">
                Reindex failed
              </Typography>
            </Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                p: 1.5,
                wordBreak: "break-all",
              }}
            >
              {error}
            </Typography>
            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                variant="contained"
                startIcon={<SyncIcon />}
                onClick={handleStart}
                sx={{ backgroundColor: "#4D8FAC", "&:hover": { backgroundColor: "#3b738a" } }}
              >
                Retry
              </Button>
              <Button variant="outlined" onClick={onClose} sx={{ color: "#dc2626", borderColor: "#dc2626" }}>
                Close
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReindexDialog;
